/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  Paper,
  MenuList,
  MenuItem,
  Typography,
  Box,
  ClickAwayListener,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import useApi from "../../../hooks/useApi";
import { useSettingValue } from "../../../services/useSettingValue";
import { GLOBALSETTINGS, SYSTEMSETTINGS, REGEX } from "../../../data/constants";
import { GlobalSearchDto, SearchRequestDto } from "../../../types/FolderPermission";
import { useUser } from "../../../context/UserContext";
import { getAllFilesFolderBySearch } from "../../../services/roleService";
import { GlobalSearchType } from "../../../data/Enums";
import "./GlobalSearchBox.scss";
import SimpleProgressBar from "../../ui/SimpleProgressBar";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import { getFileExtension, renderSvgIcon } from "../../../utils/commonHelper";
import { useNavigate, useLocation } from "react-router-dom";
import { useMenu } from "../../../context/MenuContext";
import { resetBreadcrumbs } from "../../../redux/breadcrumbSlice";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedFolder } from "../../../redux/selectedFolderSlice";
import { FOLDERTREEMESSAGES } from "../../../constants/messages";
import { RootState } from "../../../redux/store";

const GlobalSearchBox: React.FC = () => {
  const [query, setQuery] = useState("");
  const [files, setFiles] = useState<GlobalSearchDto[]>([]);
  const [menuItems, setMenuItems] = useState<GlobalSearchDto[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<GlobalSearchDto[]>([]);
  const [filteredFolders, setFilteredFolders] = useState<GlobalSearchDto[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { loading, executeApi } = useApi();
  const { userId } = useUser();
  const settingValue = useSettingValue(SYSTEMSETTINGS.RESOURCE_PAGINATION);
  const totalRecordsRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { menu } = useMenu();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [archivedItems, setArchivedItems] = useState<GlobalSearchDto[]>([]);
  const dispatch = useDispatch();
  const clearSignal = useSelector(
    (state: RootState) => state.clearSearchTextSlice.clearSearchSignal,
  );
  const searchRequestIdRef = useRef(0);
  const isFirstLocationRef = useRef(true);

  const resetSearch = useCallback(() => {
    searchRequestIdRef.current += 1;
    setQuery("");
    setOpen(false);
    setFiles([]);
    setSearchDone(false);
    setMobileOpen(false);
    setPage(1);
  }, []);

  const fetchFiles = useCallback(async () => {
    if (!query?.trim() || query?.length <= GLOBALSETTINGS.MIN_GLOBAL_SEARCH) return;
    const requestId = searchRequestIdRef.current;
    const requestData: SearchRequestDto = {
      userId,
      searchTerm: query,
      pageNumber: page,
      pageSize: settingValue,
    };
    // Store current scroll position
    const currentScrollTop = resultsRef.current?.scrollTop || 0;
    setOpen(true);
    const menuResults = menu.flatMap((section) =>
      section.listItems
        .filter((item) => item.title.toLowerCase().includes(query.toLowerCase()))
        .map((item) => ({
          id: `menu-${item.id}`, // Unique ID for menu items
          description: item.title,
          url: item.url,
          type: GlobalSearchType[GlobalSearchType.Menu],
          highlightedDescription: item.title.replace(
            new RegExp(`(${query})`, "gi"),
            `<mark>$1</mark>`,
          ),
          parentSection: section.title === "" ? "File Explorer" : section.title,
          parentIcon: section.icon,
          icon: item.icon,
        })),
    );

    const allFilesFolders = await executeApi<SearchRequestDto, GlobalSearchDto[]>(
      getAllFilesFolderBySearch,
      requestData,
    );

    // Ignore stale results if search was cleared (e.g. user navigated away)
    if (requestId !== searchRequestIdRef.current) return;

    setFiles(() => {
      if (
        allFilesFolders &&
        allFilesFolders.length === 0 &&
        menuResults &&
        menuResults.length === 0
      ) {
        return [];
      }

      const uniqueFiles = new Map();

      allFilesFolders?.forEach((file) => {
        const safeQuery = query.replace(REGEX.REGEX_ESCAPE_PATTERN, REGEX.REGEX_ESCAPE_REPLACEMENT);
        const regex = new RegExp(`(${safeQuery})`, "gi");
        file.highlightedDescription = file.description
          ? file.description.replace(regex, (match) => `<mark>${match}</mark>`)
          : file.description;
        uniqueFiles.set(String(file.id), file);
      });

      menuResults?.forEach((item) => {
        if (!uniqueFiles.has(String(item.id))) {
          uniqueFiles.set(String(item.id), item);
        }
      });

      return Array.from(uniqueFiles.values()).sort(
        (a, b) => a.description?.localeCompare(b.description || "") ?? 0,
      );
    });

    if (allFilesFolders?.length > 0) {
      totalRecordsRef.current = allFilesFolders[0].totalRecordCount;
    }

    setHasMore(() => {
      return files.length < totalRecordsRef.current;
    });

    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollTop = currentScrollTop;
      }
    }, 0);
    setSearchDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, settingValue]);

  useEffect(() => {
    setMenuItems(files.filter((file) => file.type === GlobalSearchType[GlobalSearchType.Menu]));
    setFilteredFiles(
      files.filter(
        (file) => file.type === GlobalSearchType[GlobalSearchType.File] && !file.isArchived,
      ),
    );
    setFilteredFolders(
      files.filter(
        (file) => file.type === GlobalSearchType[GlobalSearchType.Folder] && !file.isArchived,
      ),
    );
    setArchivedItems(files.filter((file) => file.isArchived));
  }, [files]);

  useEffect(() => {
    if (!query?.trim() || query.length <= GLOBALSETTINGS.MIN_GLOBAL_SEARCH) {
      setFiles([]);
      setSearchDone(false);
      return;
    }

    setSearchDone(false);

    // debounce (500ms after last keystroke)
    const handler = setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollTop = 0;
      }
      setPage(1);
      fetchFiles();
    }, 500);

    // clear timeout if user types again before 300ms
    return () => clearTimeout(handler);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Clear global search when navigating to another page/folder
  useEffect(() => {
    if (isFirstLocationRef.current) {
      isFirstLocationRef.current = false;
      return;
    }
    resetSearch();
  }, [location.pathname, location.search, resetSearch]);

  // Clear when left-nav / folder tree dispatches triggerClearSearch
  useEffect(() => {
    if (clearSignal > 0) {
      resetSearch();
    }
  }, [clearSignal, resetSearch]);

  const handleScroll = useCallback(() => {
    if (!resultsRef.current || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = resultsRef.current;
    // Calculate the maximum number of pages
    const maxPages = Math.ceil(totalRecordsRef.current / settingValue);
    // Only load more if we haven't exceeded the total pages
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && page < maxPages) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, loading, page, settingValue]);

  const handleClear = () => {
    resetSearch();
  };

  const handleMobileSearchOpen = () => {
    setMobileOpen(true);
  };

  const handleMobileSearchClose = () => {
    resetSearch();
  };

  useEffect(() => {
    const currentRef = resultsRef.current;
    if (!currentRef) return;
    currentRef.addEventListener("scroll", handleScroll);
    return () => currentRef.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const getParentFolderId = (url: string): number | null => {
    const params = new URLSearchParams(new URL(url, window.location.origin).search);
    const parentFolderId = params.get("parentFolderId");
    return parentFolderId !== null ? Number(parentFolderId) : null;
  };

  const navigateToItem = (url: string) => {
    dispatch(resetBreadcrumbs());
    dispatch(
      setSelectedFolder({
        folderId: null,
        folderName: FOLDERTREEMESSAGES.ROOT_FOLDER,
        canEdit: true,
      }),
    );
    navigate(`${url}`);
    resetSearch();
  };

  const hasAnyResults =
    menuItems.length > 0 ||
    filteredFiles.length > 0 ||
    filteredFolders.length > 0 ||
    archivedItems.length > 0;

  // Render the search input (for desktop and mobile dialog)
  const renderSearchInput = () => (
    <TextField
      id="globalSearch"
      placeholder="Search"
      variant="outlined"
      value={query}
      onChange={(e) => {
        setOpen(true);
        setQuery(e.target.value);
      }}
      fullWidth
      autoFocus={isMobile}
      sx={{
        height: "40px",
        backgroundColor: "#FFFFFF",
        borderRadius: "50px",
        "& .MuiOutlinedInput-root": {
          height: "40px",
          borderRadius: "50px",
          color: "#000000",
          "& fieldset": { border: "none" },
          "&:hover fieldset": { border: "none" },
          "&.Mui-focused fieldset": { border: "none" },
        },
        "& input": { padding: "10px 14px" },
        "& input::placeholder": { color: "#000000", opacity: 1 },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: "#000000" }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <SimpleProgressBar loading={loading} size={30} />
            {query && (
              <Tooltip title="Clear search">
                <IconButton onClick={handleClear} edge="end" className="clear-button">
                  <ClearIcon className="clear-icon" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
    />
  );

  // Render the search results — hide empty Paper while loading (avoids white bar)
  const renderSearchResults = () =>
    query?.length > GLOBALSETTINGS.MIN_GLOBAL_SEARCH &&
    open &&
    (hasAnyResults || searchDone) && (
      <ClickAwayListener
        onClickAway={() => {
          setOpen(false);
        }}
      >
        <Paper
          ref={resultsRef}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 300,
            overflowY: "auto",
            mt: 1,
            zIndex: 1000,
            border: "1px solid #ddd",
            borderRadius: "4px",
          }}
        >
          <MenuList>
            {/* Render Tools Section */}
            {menuItems.length > 0 && (
              <Box sx={{ px: 2, py: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                  Tools
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", pl: 3 }}>
                  {menuItems.map(
                    (file) =>
                      file.parentSection && (
                        <React.Fragment key={file.id}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: "text.primary",
                              mt: 1,
                              fontSize: "1rem",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <img src={file.parentIcon} alt="Tools Icon" width="17px" />
                              {file.parentSection}
                            </Box>
                          </Typography>
                          <MenuItem
                            sx={{ pl: 4 }}
                            onClick={() => {
                              if (file.url) {
                                navigateToItem(`${file.url}`);
                              }
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <img src={file.icon} alt="Tools Icon" className="file-icon-small" />
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: file.highlightedDescription?.replace(
                                    /<mark>/g,
                                    `<mark class="mark">`,
                                  ),
                                }}
                              />
                            </Box>
                          </MenuItem>
                        </React.Fragment>
                      ),
                  )}
                </Box>
              </Box>
            )}

            {/* Render Items (Files and Folders) */}
            {(filteredFiles.length > 0 || filteredFolders.length > 0) && (
              <Box sx={{ px: 2, py: 0.5, mt: -3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", color: "text.primary", mt: 2, fontSize: "1.25rem" }}
                >
                  Items
                </Typography>

                {filteredFiles.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", pl: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "text.primary", mt: 1, fontSize: "1rem" }}
                    >
                      📄 Files
                    </Typography>
                    {filteredFiles.map((file) => (
                      <MenuItem
                        key={file.id}
                        sx={{ pl: 4 }}
                        onClick={() => {
                          if (file.url) {
                            navigateToItem(`${GLOBALSETTINGS.DEFAULT_PAGE}${file.url}`);
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {renderSvgIcon(getFileExtension(file.description), "file-icon-small", 16)}
                          <span
                            dangerouslySetInnerHTML={{
                              __html: file.highlightedDescription?.replace(
                                /<mark>/g,
                                `<mark class="mark">`,
                              ),
                            }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Box>
                )}

                {filteredFolders.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", pl: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "text.primary", mt: 1, fontSize: "1rem" }}
                    >
                      📁 Folders
                    </Typography>
                    {filteredFolders.map((folder) => (
                      <MenuItem
                        key={folder.id}
                        sx={{ pl: 4 }}
                        onClick={() => {
                          if (folder.url) {
                            const parentFolderId = getParentFolderId(folder.url);
                            const page =
                              parentFolderId === -1 || parentFolderId === null
                                ? GLOBALSETTINGS.DEFAULT_PAGE
                                : GLOBALSETTINGS.LIST_DETAIL_PAGE;
                            navigateToItem(`${page}${folder.url}`);
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <img src="/folder.svg" alt="Folder Icon" className="file-icon-small" />
                          <span
                            dangerouslySetInnerHTML={{
                              __html: folder.highlightedDescription?.replace(
                                /<mark>/g,
                                `<mark class="mark">`,
                              ),
                            }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {archivedItems.length > 0 && (
              <Box sx={{ px: 2, py: 0.5, mt: -3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", color: "text.primary", mt: 2, fontSize: "1.25rem" }}
                >
                  Recycle Bin
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", pl: 3 }}>
                  {archivedItems.map((item) => (
                    <MenuItem
                      key={item.id}
                      sx={{ pl: 4 }}
                      onClick={() => {
                        if (item.url) {
                          const page = GLOBALSETTINGS.RECYCLEBIN_PAGE;
                          if (item.topLevelFolderId) {
                            navigateToItem(`${page}?topLevelId=${item.topLevelFolderId}`);
                          } else {
                            navigateToItem(`${page}${item.url}`);
                          }
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* Show file/folder icon based on type */}
                        {item.type === GlobalSearchType[GlobalSearchType.File] ? (
                          renderSvgIcon(getFileExtension(item.description), "file-icon-small", 16)
                        ) : (
                          <img src="/folder.svg" alt="Folder Icon" className="file-icon-small" />
                        )}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: item.highlightedDescription?.replace(
                              /<mark>/g,
                              `<mark class="mark">`,
                            ),
                          }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Box>
              </Box>
            )}

            {/* Display 'No Results' if nothing to show */}
            {!loading &&
              menuItems.length === 0 &&
              filteredFiles.length === 0 &&
              filteredFolders.length === 0 &&
              archivedItems.length === 0 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No results found
                  </Typography>
                </Box>
              )}
          </MenuList>
        </Paper>
      </ClickAwayListener>
    );

  return (
    <>
      {/* Desktop View */}
      {!isMobile && (
        <Box sx={{ position: "relative", width: "611px" }}>
          {renderSearchInput()}
          {renderSearchResults()}
        </Box>
      )}

      {/* Mobile View */}
      {isMobile && (
        <>
          <IconButton
            onClick={handleMobileSearchOpen}
            sx={{
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <SearchIcon sx={{ color: "#fff", fontSize: "1.5rem" }} />
          </IconButton>

          <Collapse in={mobileOpen} timeout={300}>
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                backgroundColor: "rgba(255, 255, 255, 0.96)",
                backdropFilter: "blur(4px)",
                zIndex: 1300,
                p: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1 }}>{renderSearchInput()}</Box>
                <IconButton
                  onClick={handleMobileSearchClose}
                  sx={{
                    color: "black",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "rotate(90deg)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box>{renderSearchResults()}</Box>
            </Box>
          </Collapse>

          {/* Overlay when search is open */}
          {mobileOpen && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1200,
              }}
              onClick={handleMobileSearchClose}
            />
          )}
        </>
      )}
    </>
  );
};

export default GlobalSearchBox;
